import { TablePreview } from './components/TablePreview';
import { TablePreviewToggle } from './components/TablePreviewToggle';
import { RestorationControls } from './components/RestorationControls';
import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import {
  playerDisplayName,
  type AuthResult,
  type BallColor,
  type Handicap,
  type RedCount,
  type PlayerProfile,
  type PlayerIndex,
  type ScoringMode,
} from '@aiscoreboard/contracts';
import { ActionBar } from './components/ActionBar';
import { AiNotice, ControlBar } from './components/ControlBar';
import { ConfirmDialog } from './components/ConfirmDialog';
import { MatchClock } from './components/MatchClock';
import { FrameOverlay } from './components/FrameOverlay';
import { FrameWinnerDialog } from './components/FrameWinnerDialog';
import { RemotePanel } from './components/RemotePanel';
import { RemoteButton } from './components/RemoteButton';
import { BreakBlock } from './components/BreakBlock';
import { FramesBlock } from './components/FramesBlock';
import { PlayerPanel } from './components/PlayerPanel';
import { SignInPanel } from './components/SignInPanel';
import { SuggestionQueue } from './components/SuggestionQueue';
import { HomeScreen } from './screens/HomeScreen';
import { JoinScreen } from './screens/JoinScreen';
import { LeaderboardScreen } from './screens/LeaderboardScreen';
import { LegalScreen } from './screens/LegalScreen';
import { MatchSetupScreen } from './screens/MatchSetupScreen';
import { PracticeScreen } from './screens/PracticeScreen';
import { ProfileScreen } from './screens/ProfileScreen';
import { CameraScreen } from './screens/CameraScreen';
import { SpectatorScreen } from './screens/SpectatorScreen';
import { listActiveDevices, listCameraDevices, listDevices, updateDevice, type Seat } from './lib/accounts';
import { controlToken } from './lib/operator';
import { getPairedYouTubeDevice, getYouTubeVenue, youtubeDevicePath, youtubeRequest } from './lib/youtube';
import { PREFERRED_DEVICE_KEY, preferredPairing } from './lib/devicePairing';
import { useYouTubeBroadcast } from './lib/useYouTubeBroadcast';
import { useLiveViewers } from './lib/useLiveViewers';
import { LiveViewerCount } from './components/LiveViewerCount';
import { ScoreEntryDialog } from './components/ScoreEntryDialog';
import { QUIET_FRAME } from './lib/accent';
import { ApiError, createMatch, fetchMatchDevices } from './lib/api';
import { LICENSE_EXPIRED_MESSAGE } from '@aiscoreboard/contracts';
import { useMatchDevices } from './lib/useMatchDevices';
import { isCameraAvailable, isCameraLicenseExpired } from './lib/camera';
import { bindCameraControl, changeCameraStreaming } from './lib/cameraControl';
import {
  clearPairFromUrl,
  clearYouTubeReturnFromUrl,
  matchIdFromUrl,
  pairCodeFromUrl,
  putMatchInUrl,
  putSettingsInUrl,
  settingsFromUrl,
  youtubeReturnFromUrl,
} from './lib/handoff';
import { useT, type MessageKey } from './lib/i18n';
import { clearStoredMatchId, loadStoredMatchId, useMatch } from './lib/useMatch';
import { useSeats } from './lib/useSeats';
import { useActiveRoom } from './lib/useActiveRoom';
import { useOperatorTimeout } from './lib/useOperatorTimeout';
import { observePlayerMatch, playerMatchEnded, playerMatchStarted } from './lib/playerSessionTimeout';

type View = 'home' | 'match-setup' | 'practice' | 'leaderboard' | 'profile' | 'impressum' | 'privacy' | 'cameras' | 'spectator';

/**
 * The match asked for by the address bar, read once when the module loads.
 *
 * Read here rather than in an effect because the effect that keeps the
 * address bar in step runs with `matchId` still null on the first pass and
 * strips `?m=` back out. React re-runs effects on mount in development, so
 * the second pass would then read an address the first pass had already
 * emptied —and a phone that had just scanned a QR code would land on the
 * home screen, which is the one journey this link exists to serve. A module
 * -level read happens before any render, so nothing can beat it to the URL.
 */
const MATCH_ID_FROM_URL = matchIdFromUrl();

/** Whether this device arrived by scanning a board's settings QR code. */
const SETTINGS_FROM_URL = settingsFromUrl();
const YOUTUBE_RETURN = youtubeReturnFromUrl();

export default function App() {
  const t = useT();
  const [view, setView] = useState<View>(YOUTUBE_RETURN ? 'cameras' : 'home');
  const [matchId, setMatchId] = useState<string | null>(
    () => YOUTUBE_RETURN || SETTINGS_FROM_URL || pairCodeFromUrl() ? null : MATCH_ID_FROM_URL ?? loadStoredMatchId(),
  );
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const [bestOf, setBestOf] = useState(1);
  const [redCount, setRedCount] = useState<RedCount>(15);
  const [timeLimitMinutes, setTimeLimitMinutes] = useState<number | null>(null);
  const [handicap, setHandicap] = useState<Handicap | null>(null);
  /** The seat the profile screen is acting on. */
  const [activeSeat, setActiveSeat] = useState<Seat>(0);
  const [signingIn, setSigningIn] = useState<Seat | null>(null);
  const [showTablePreview, setShowTablePreview] = useState(false);
  const [remaking, setRemaking] = useState(false);
  const [handingOff, setHandingOff] = useState(false);
  const [scoreEntry, setScoreEntry] = useState<{ matchId: string; frameNumber: number; player: PlayerIndex } | null>(null);
  /** Set while the operator is confirming that they want to abandon the match. */
  const [leaving, setLeaving] = useState(false);
  /** Set when this device arrived by scanning a scoreboard's pairing QR code. */
  const [pairCode, setPairCode] = useState<string | null>(() => pairCodeFromUrl());
  /** Why a seat refused the last account offered to it, per seat. */
  const [seatNotices, setSeatNotices] = useState<[string | null, string | null]>([null, null]);
  const [streamToggleError, setStreamToggleError] = useState<string | null>(null);
  const [aiNotice, setAiNotice] = useState<string | null>(null);
  const [streamTogglePending, setStreamTogglePending] = useState(false);
  const streamToggleInFlight = useRef(false);
  const endMatchInFlight = useRef(false);
  const streamToggleErrorTimer = useRef<number | null>(null);
  /**
   * The account signed in on a phone that scanned a pairing or settings code.
   *
   * Kept in memory rather than in storage: this is somebody editing their
   * account at a table, and a phone that stays signed in to a settings screen
   * is a phone somebody else can pick up.
   */
  const [phoneSession, setPhoneSession] = useState<{ token: string; player: PlayerProfile } | null>(
    null,
  );
  const [phoneSettings, setPhoneSettings] = useState(SETTINGS_FROM_URL);

  const { seats, ready, signIn, signOut, update } = useSeats(matchId !== null);
  const roomError = useActiveRoom(!pairCode && !phoneSettings
    && (view === 'home' || view === 'match-setup'), matchId, setMatchId);
  const {
    state,
    connected,
    suggestions,
    error,
    notice,
    savingSlow,
    saving,
    dispatch,
    undoLast,
    returnToOffender,
    recordFoul,
    resolveSuggestion,
    finishFrame,
    frameWinnerRequired,
    cancelFrameWinner,
    refresh,
    startNextFrame,
  } = useMatch(matchId);
  useOperatorTimeout(ready && !pairCode && !phoneSettings && signingIn === null
    && (!matchId || !state) && !(matchId && error) && view === 'cameras');
  const youtube = useYouTubeBroadcast(matchId);
  const liveViewers = useLiveViewers(matchId);
  useEffect(() => {
    setScoreEntry(entry => entry && (entry.matchId !== state?.matchId
      || entry.frameNumber !== state?.frameNumber || state?.status !== 'in_progress') ? null : entry);
  }, [state?.matchId, state?.frameNumber, state?.status]);
  useEffect(() => {
    setRemaking(false);
  }, [state?.matchId, state?.frameNumber, state?.status]);
  const matchDevices = useMatchDevices(matchId);
  const hasExpiredCamera = Boolean(matchDevices?.data?.devices.some(isCameraLicenseExpired));
  const currentMatch = useRef({ id: matchId, status: state?.status });
  currentMatch.current = { id: matchId, status: state?.status };
  const canControlMatch = (id: string | null): boolean =>
    id !== null && currentMatch.current.id === id && currentMatch.current.status !== 'match_over';

  const showCameraNotFound = useCallback((message: MessageKey = 'match.cameraNotFound'): void => {
    setStreamToggleError(t(message));
    if (streamToggleErrorTimer.current !== null) {
      window.clearTimeout(streamToggleErrorTimer.current);
    }
    streamToggleErrorTimer.current = window.setTimeout(() => {
      setStreamToggleError(null);
      streamToggleErrorTimer.current = null;
    }, message === 'match.licenseExpired' ? 7000 : 3000);
  }, [t]);

  useEffect(() => {
    return () => {
      if (streamToggleErrorTimer.current !== null) {
        window.clearTimeout(streamToggleErrorTimer.current);
      }
    };
  }, []);

  /** Keep the address bar in step, so the page can be shared or reloaded. */
  useEffect(() => {
    if (!pairCode && !phoneSettings) putMatchInUrl(matchId);
  }, [matchId, pairCode, phoneSettings]);

  // Opening a page is read-only: another browser's preferences or heartbeat
  // observations must never unbind a shared camera or switch its broadcast off.

  useEffect(() => {
    if (state && state.matchId === matchId) observePlayerMatch(state);
  }, [state, matchId]);

  /**
   * Seat an account, or say why not.
   *
   * Both routes into a seat — typing a password on the board and scanning the
   * seat's QR code with a phone — end here, so the "one account, one seat"
   * rule is stated once and cannot be walked around by taking the other door.
   */
  const claimSeat = (seat: Seat, auth: AuthResult): boolean => {
    const outcome = signIn(seat, auth);
    setSeatNotices((prev) => {
      const next: [string | null, string | null] = [...prev];
      next[seat] = outcome.ok ? null : t('seat.alreadySeated', { n: outcome.taken + 1 });
      return next;
    });
    return outcome.ok;
  };

  /** Drop a seat's refusal once the situation it described has changed. */
  const clearSeatNotice = (seat: Seat): void => {
    setSeatNotices((prev) => {
      if (!prev[seat]) return prev;
      const next: [string | null, string | null] = [...prev];
      next[seat] = null;
      return next;
    });
  };

  const leave = (): void => {
    clearStoredMatchId();
    setMatchId(null);
    setRemaking(false);
    setHandingOff(false);
    setLeaving(false);
    setView('home');
  };

  const endAndLeave = async (): Promise<void> => {
    if (endMatchInFlight.current || !state) return;
    endMatchInFlight.current = true;
    setLeaving(false);
    try {
      if (state.status !== 'match_over') {
        const [a, b] = state.framesWon;
        const saved = await dispatch({
          type: 'match.ended',
          source: 'manual',
          ...(a === b ? {} : { winner: a > b ? 0 : 1 }),
        });
        if (!saved) return;
      }
      playerMatchEnded(state.matchId);
      leave();
    } finally {
      endMatchInFlight.current = false;
    }
  };

  const start = async (
    players: [string, string],
    playerIds: [string | null, string | null],
    countries: [string | null, string | null],
  ): Promise<void> => {
    setStarting(true);
    setStartError(null);
    setStreamToggleError(null);
    try {
      const deviceId = localStorage.getItem(PREFERRED_DEVICE_KEY);
      const token = controlToken();
      const paired = preferredPairing();
      const owned = paired ? (await getPairedYouTubeDevice(paired.token, paired.deviceId)).device
        : token && deviceId ? (await listDevices(token)).devices.find(device => device.id === deviceId) : undefined;
      let selected = owned;
      const match = await createMatch(
        players,
        bestOf,
        playerIds,
        countries,
        timeLimitMinutes,
        handicap,
        owned?.venueId,
        !selected || (selected.license?.enforcementEnabled && selected.license.status !== 'active') ? false : undefined,
        redCount,
      );
      playerMatchStarted(match.id);
      setMatchId(match.id);
      if (match.reused) return;
      // Creating a match is an explicit table action, unlike visiting a link.
      if (deviceId && !paired && selected && owned) {
        try {
          if (token && owned) await updateDevice(token, deviceId, { matchId: match.id });
          if (token && owned?.youtubeManaged && (!owned.license?.enforcementEnabled || owned.license.status === 'active')) {
            await youtubeRequest(token, `${youtubeDevicePath(deviceId)}/start`, 'POST', { matchId: match.id });
          }
        } catch (err) {
          console.warn('[camera-bind] failed', err);
          setStreamToggleError(err instanceof Error ? err.message : t('match.cameraNotFound'));
        }
      }
    } catch (e) {
      setStartError(e instanceof Error ? e.message : 'Could not start the match');
    } finally {
      setStarting(false);
    }
  };

  const openPractice = (): void => {
    // A guest can practise: nothing is saved, but a player who just wants to
    // count their own break should not have to make an account to do it.
    setActiveSeat(0);
    setView('practice');
  };

  // A pairing code beats everything else this device might have been doing:
  // somebody has just scanned a scoreboard and is standing there waiting.
  if (pairCode !== null) {
    return (
      <JoinScreen
        code={pairCode}
        onJoined={(auth) => {
          setPhoneSession(auth);
          setPhoneSettings(true);
          putSettingsInUrl();
          setPairCode(null);
        }}
        onCancel={() => {
          clearPairFromUrl();
          setPairCode(null);
        }}
      />
    );
  }

  // Sent here by a board's pairing or settings code. The phone signs in on its own — no
  // session travels in the QR — and then edits the account it just proved it
  // owns, on a screen nobody else is looking at.
  if (phoneSettings) {
    if (!phoneSession) {
      return (
        <main className="grid h-full place-items-center p-4">
          <SignInPanel
            title={t('profile.title')}
            onDone={setPhoneSession}
            onCancel={() => {
              location.href = location.pathname;
            }}
          />
        </main>
      );
    }
    return (
      <ProfileScreen
        onPhone
        token={phoneSession.token}
        player={phoneSession.player}
        onUpdated={(p) => setPhoneSession({ ...phoneSession, player: p })}
        onClosed={() => setPhoneSession(null)}
        onBack={() => setPhoneSession(null)}
      />
    );
  }

  if (signingIn !== null) {
    const seat = signingIn;
    return (
      <main className="grid h-full place-items-center p-4">
        <SignInPanel
          title={t('seat.player', { n: seat + 1 })}
          onDone={(auth) => {
            claimSeat(seat, auth);
            setSigningIn(null);
          }}
          onCancel={() => {
            setSigningIn(null);
          }}
        />
      </main>
    );
  }

  if (!matchId || !state) {
    if (!ready) {
      return <main className="grid h-full place-items-center text-white/50">{t('common.loading')}</main>;
    }

    // A shared link can outlive its match, and the home screen would silently
    // swallow that. Say so instead, and offer a way out of the dead link.
    if (matchId && error) {
      return (
        <main className="flex h-full flex-col items-center justify-center gap-4 p-6 text-center">
          <h1 className="text-2xl font-black">{t('match.unavailable')}</h1>
          <p className="max-w-sm text-white/60">{error}</p>
          <p className="font-mono text-xs text-white/30">{matchId}</p>
          <button type="button" onClick={leave} className="btn bg-white text-hud-bg">
            {t('match.backToStart')}
          </button>
        </main>
      );
    }

    const active = seats[activeSeat];

    if (view === 'profile' && active) {
      return (
        <ProfileScreen
          token={active.token}
          player={active.player}
          onUpdated={(p) => update(activeSeat, p)}
          onClosed={() => {
            signOut(activeSeat);
            setView('home');
          }}
          onBack={() => setView('home')}
        />
      );
    }

    if (view === 'leaderboard') {
      const me = seats[0] ?? seats[1];
      return (
        <LeaderboardScreen
          token={me?.token ?? null}
          home={
            me
              ? {
                  country: me.player.club?.country ?? me.player.country,
                  city: me.player.club?.city ?? null,
                }
              : null
          }
          onBack={() => setView('home')}
        />
      );
    }

    if (view === 'impressum' || view === 'privacy') {
      return (
        <LegalScreen kind={view === 'impressum' ? 'impressum' : 'privacy'} onBack={() => setView('home')} />
      );
    }

    if (view === 'practice') {
      return (
        <PracticeScreen
          session={seats[0]}
          name={seats[0] ? playerDisplayName(seats[0].player) : t('seat.guest')}
          onSignIn={() => setSigningIn(0)}
          onSignOut={() => signOut(0)}
          onSettings={() => {
            setActiveSeat(0);
            setView('profile');
          }}
          onPaired={(auth) => {
            claimSeat(0, auth);
          }}
          onBack={() => setView('home')}
        />
      );
    }

    if (view === 'match-setup') {
      return (
        <MatchSetupScreen
          seats={seats}
          bestOf={bestOf}
          onBestOfChange={setBestOf}
          redCount={redCount}
          onRedCountChange={setRedCount}
          timeLimitMinutes={timeLimitMinutes}
          onTimeLimitChange={setTimeLimitMinutes}
          handicap={handicap}
          onHandicapChange={setHandicap}
          onSignIn={setSigningIn}
          onSignOut={(seat) => {
            signOut(seat);
            // Signing anybody out can only make a refusal stale: the account
            // that was "already seated" may be the one that just left.
            clearSeatNotice(0);
            clearSeatNotice(1);
          }}
          onPaired={(seat, auth) => {
            claimSeat(seat, auth);
          }}
          notices={seatNotices}
          onSettings={(seat) => {
            setActiveSeat(seat);
            setView('profile');
          }}
          onStart={(players, ids, countries) => void start(players, ids, countries)}
          onBack={() => setView('home')}
          starting={starting}
          error={startError ?? roomError ?? error}
        />
      );
    }

    if (view === 'cameras') {
      return (
        <CameraScreen
          onBack={() => {
            clearYouTubeReturnFromUrl();
            setView('home');
          }}
        />
      );
    }
    if (view === 'spectator') {
      return <SpectatorScreen onBack={() => setView('home')} />;
    }

    return (
      <HomeScreen
        error={roomError}
        onMatch={() => setView('match-setup')}
        onPractice={openPractice}
        onLeaderboards={() => setView('leaderboard')}
        onCameras={() => setView('cameras')}
        onSpectate={() => setView('spectator')}
        onImpressum={() => setView('impressum')}
        onPrivacy={() => setView('privacy')}
      />
    );
  }

  const live = state.status === 'in_progress';
  const statusMessage = error ?? roomError ?? notice ?? (savingSlow ? t('match.saving') : null);
  // With no points left on the table there is nothing to score and nothing to
  // concede: potting and fouling both stop, and ending the frame is all that
  // is left.
  const exhausted = state.pointsAvailable === 0;
  const paused = state.paused;
  const pauseNotice =
    state.pauseMessage ??
    (state.pauseReason === 'waiting_for_respot'
      ? t('match.pausedWaitingRespot')
      : t('match.paused'));

  const pot = (color: BallColor, count: number, freeBall = false): void => {
    void dispatch({
      type: 'shot.pot',
      source: 'manual',
      player: state.activePlayer,
      color,
      count,
      freeBall,
    });
  };

  /**
   * Award or withdraw a free ball for the player at the table.
   *
   * Its own event rather than a flag on the foul: whether the incoming player
   * is snookered on every ball on is a question about the table once the balls
   * have stopped, which is asked after the foul has already been entered.
   */
  const setFreeBall = (awarded: boolean): void => {
    void dispatch({
      type: 'freeball.awarded',
      source: 'manual',
      player: state.activePlayer,
      awarded,
    });
  };

  const miss = (): void => {
    void dispatch({ type: 'shot.miss', source: 'manual', player: state.activePlayer });
  };

  /**
   * Play this frame again, from nothing, without awarding it.
   *
   * A re-rack is a real thing that happens at a table —a disputed frame, a
   * ball knocked off by a passer-by, a match restarted after a fault —and
   * before this the only way to get back to nought was to award the frame to
   * somebody who had not won it and then live with a wrong match score for the
   * rest of the night.
   *
   * It is expressed as a second `frame.started` with the *same* frame number,
   * which the reducer already handles: the scores, the visits, the reds and
   * the break are reset and `framesWon` is not touched, because only
   * `frame.ended` ever moves that. Nothing is deleted either —the abandoned
   * frame stays in the event log, which is what an audit of a disputed frame
   * needs.
   */
  const remakeFrame = (): void => {
    void dispatch({
      type: 'frame.started',
      source: 'manual',
      frameNumber: state.frameNumber,
      // The breaker alternates by frame number, so replaying a frame has to
      // hand the break back to whoever opened it the first time.
      startingPlayer: ((state.frameNumber - 1) % 2) as 0 | 1,
    });
  };

  const foul = (points: number): void => { void recordFoul(points); };

  const setMode = (mode: ScoringMode): void => {
    if (mode !== 'assisted') {
      void dispatch({ type: 'mode.changed', source: 'manual', mode });
      return;
    }
    void (async () => {
      try {
        if (!matchId) return;
        const bound = await fetchMatchDevices(matchId);
        if (!canControlMatch(matchId)) return;
        if (!bound.aiSupported) {
          setAiNotice(t('match.aiUnsupported'));
          return;
        }
        if (bound.devices.length > 0) {
          if (!bound.devices.some(isCameraAvailable)) {
            showCameraNotFound();
            return;
          }
          void dispatch({ type: 'mode.changed', source: 'manual', mode: 'assisted' });
          return;
        }
        const preferredDeviceId = localStorage.getItem(PREFERRED_DEVICE_KEY);
        if (!preferredDeviceId) {
          showCameraNotFound();
          return;
        }
        const paired = preferredPairing();
        const devices = paired ? [(await getPairedYouTubeDevice(paired.token, paired.deviceId)).device]
          : (await listActiveDevices()).devices;
        if (!canControlMatch(matchId)) return;
        const preferred = devices.find((device) => device.id === preferredDeviceId);
        if (!preferred || !isCameraAvailable(preferred)) {
          showCameraNotFound();
          return;
        }
        if (preferred.aiSupported === false) {
          setAiNotice(t('match.aiUnsupported'));
          return;
        }
        const token = controlToken();
        if (paired && paired.deviceId === preferredDeviceId) {
          if (!canControlMatch(matchId)) return;
          await bindCameraControl({ device: preferred, token: paired.token, paired: true }, matchId);
        } else if (token && (await listDevices(token)).devices.some(device => device.id === preferredDeviceId)) {
          if (!canControlMatch(matchId)) return;
          await bindCameraControl({ device: preferred, token, paired: false }, matchId);
        } else {
          if (!canControlMatch(matchId)) return;
          showCameraNotFound('youtube.loginRequired');
          return;
        }
        if (!canControlMatch(matchId)) return;
        void dispatch({ type: 'mode.changed', source: 'manual', mode: 'assisted' });
      } catch (err) {
        console.warn('[ai-mode] device bind failed', err);
        setStreamToggleError(err instanceof Error ? err.message : String(err));
      }
    })();
  };

  const setStreaming = (enabled: boolean): void => {
    if (!matchId || streamToggleInFlight.current) return;
    streamToggleInFlight.current = true;
    setStreamTogglePending(true);
    void (async () => {
      try {
        const token = controlToken();
        const deviceId = localStorage.getItem(PREFERRED_DEVICE_KEY);
        const paired = preferredPairing();
        if (paired) {
          const selected = await getPairedYouTubeDevice(paired.token, paired.deviceId);
          if (!canControlMatch(matchId)) return;
          if (enabled && isCameraLicenseExpired(selected.device)) { showCameraNotFound('match.licenseExpired'); return; }
          if (selected.device.youtubeManaged && !selected.broadcast.connectionId) throw new Error(t('youtube.authorizeFirst'));
          if (enabled && !isCameraAvailable(selected.device)) { showCameraNotFound(); return; }
          await changeCameraStreaming({ device: selected.device, token: paired.token, paired: true }, matchId, enabled, dispatch);
          setStreamToggleError(null);
          await youtube.refresh();
          return;
        }
        if (!token) {
          showCameraNotFound('youtube.loginRequired');
          return;
        }
        if (token) {
          const { devices } = await listDevices(token);
          const selected = enabled ? devices.find(device => device.id === deviceId)
            : devices.find(device => device.id === youtube.device?.deviceId)
              ?? devices.find(device => device.id === deviceId && device.matchId === matchId);
          if (selected) {
            if (!canControlMatch(matchId)) return;
            if (enabled && isCameraLicenseExpired(selected)) { showCameraNotFound('match.licenseExpired'); return; }
            const venue = await getYouTubeVenue(token, selected.venueId);
            const managed = venue.devices.find(device => device.deviceId === selected.id && device.connectionId);
            if (!canControlMatch(matchId)) return;
            if (managed) {
              if (enabled && !isCameraAvailable(selected)) { showCameraNotFound(); return; }
              await changeCameraStreaming({ device: selected, token, paired: false }, matchId, enabled, dispatch);
              setStreamToggleError(null);
              await youtube.refresh();
              return;
            }
            if (selected.youtubeManaged) throw new Error(t('youtube.authorizeFirst'));
          }
        }
        const bound = await fetchMatchDevices(matchId);
        if (!canControlMatch(matchId)) return;
        if (enabled && bound.devices.some(isCameraLicenseExpired)) { showCameraNotFound('match.licenseExpired'); return; }
        if (bound.devices.some(device => device.youtubeManaged)) {
          showCameraNotFound('youtube.loginRequired');
          return;
        }
        if (enabled) {
          const preferredDeviceId = localStorage.getItem(PREFERRED_DEVICE_KEY);
          if (!preferredDeviceId) {
            showCameraNotFound();
            return;
          }
          const { devices } = await listCameraDevices();
          if (!canControlMatch(matchId)) return;
          const preferred = devices.find((d) => d.id === preferredDeviceId);
          if (isCameraLicenseExpired(preferred)) { showCameraNotFound('match.licenseExpired'); return; }
          if (preferred?.youtubeManaged) {
            showCameraNotFound('youtube.loginRequired');
            return;
          }
          if (!isCameraAvailable(preferred)) {
            showCameraNotFound();
            return;
          }
          if (!preferred?.rtmpUrl || !preferred.hasStreamKey) {
            showCameraNotFound('match.cameraStreamingNotReady');
            return;
          }
          if (token) {
            const owned = (await listDevices(token)).devices.some(device => device.id === preferredDeviceId);
            if (!canControlMatch(matchId)) return;
            if (owned) await updateDevice(token, preferredDeviceId, { matchId });
            else { showCameraNotFound('youtube.loginRequired'); return; }
          }
        }
        if (!canControlMatch(matchId)) return;
        setStreamToggleError(null);
        await dispatch({ type: 'streaming.changed', source: 'manual', enabled });
      } catch (err) {
        console.warn('[camera-streaming] toggle failed', err);
        if (err instanceof ApiError && err.status === 403 && err.message === LICENSE_EXPIRED_MESSAGE) {
          showCameraNotFound('match.licenseExpired');
        } else setStreamToggleError(err instanceof Error ? err.message : String(err));
      } finally {
        streamToggleInFlight.current = false;
        setStreamTogglePending(false);
      }
    })();
  };

  const streamingEnabled = !hasExpiredCamera && (youtube.device
    ? ['preparing', 'waiting_for_stream', 'live'].includes(youtube.device.status)
    : state.streaming);
  const visibleLiveViewers = streamingEnabled && state.streaming && state.status !== 'match_over'
    && (!youtube.device || youtube.device.status === 'live') ? liveViewers : null;

  return (
    // The board fills whatever screen it is on: no fixed aspect ratio, just
    // one responsive layout that can grow and shrink with the viewport.
    <div className="board-viewport">
      <main
        className="board-shell live-shell relative mx-auto flex w-full max-w-[min(140rem,95vw)] flex-col gap-2
                   p-2 sm:gap-3 sm:p-3"
      >
      {/* Keep request feedback visible without moving or intercepting the controls. */}
      {statusMessage && (
        <div
          role={error ? 'alert' : 'status'}
          className="hud-frame hud-frame-sm pointer-events-none absolute bottom-[20.5%] left-1/2
                     z-20 w-max max-w-[90%] -translate-x-1/2 px-5 py-2.5 text-center text-sm font-medium"
          style={QUIET_FRAME as CSSProperties}
        >
          <span className={error ? 'text-sys-red' : 'text-white/85'}>{statusMessage}</span>
        </div>
      )}
      <ControlBar
        tablePreview={state.mode === 'assisted' && <TablePreviewToggle pressed={showTablePreview}
          onToggle={() => setShowTablePreview(value => !value)} />}
        mode={state.mode}
        aiUnavailableReason={matchDevices?.error ? t('match.aiDeviceCheckFailed')
          : !matchDevices?.data ? t('match.aiDeviceChecking')
          : !matchDevices.data.aiSupported ? t('match.aiUnsupported') : null}
        streaming={streamingEnabled}
        streamingPending={streamTogglePending || (!hasExpiredCamera && youtube.device?.status === 'stopping')}
        clock={<MatchClock state={state} />}
        onModeChange={setMode}
        onStreamingChange={setStreaming}
        onLeave={() => setLeaving(true)}
        onRemakeFrame={() => setRemaking(true)}
        remakeDisabled={!live || paused || saving}
      />
      {(youtube.device || youtube.error || (visibleLiveViewers && visibleLiveViewers.status !== 'offline')) && <div className="flex flex-wrap items-center justify-center gap-3 text-xs text-white/70">
        {youtube.device && <span role="status">{t(`youtube.status.${youtube.device.status}`)}</span>}
        <LiveViewerCount data={visibleLiveViewers} />
        {(youtube.error || youtube.device?.error) && <span role="alert" className="text-red-300">
          {youtube.error ?? youtube.device?.error}
        </span>}
        {streamingEnabled && state.streaming && youtube.watchUrl && <a className="text-cyan-300 underline" href={youtube.watchUrl}
          target="_blank" rel="noopener noreferrer">{t('youtube.watch')}</a>}
      </div>}

      <div className="match-live flex flex-col items-stretch gap-2 sm:gap-3 md:flex-[5] md:flex-row">
        <div className="relative flex flex-1 flex-col min-h-0">
          <PlayerPanel
            state={state}
            player={0}
            avatarUrl={seats[0]?.player.avatarUrl ?? null}
            onTakeTable={live ? miss : undefined}
            onAddScore={() => setScoreEntry({ matchId, frameNumber: state.frameNumber, player: 0 })}
          />
          {streamToggleError && (
            <div
              className="pointer-events-none absolute left-0 top-[2.125rem] z-50 flex w-[28%]
                         -translate-y-1/2 justify-center px-3 sm:px-5"
            >
              <div
                role="status"
                className="hud-frame hud-frame-sm w-fit max-w-full whitespace-normal break-words
                           px-2.5 py-1 text-center text-[0.7rem] font-semibold leading-tight
                           text-sys-red sm:text-xs"
              >
                {streamToggleError}
              </div>
            </div>
          )}
        </div>

        {/* Two cards, not one. The frame score is the state of the *match* and
            changes twice an hour; the break and what is left on the table are
            the state of the *moment* and change every shot. They used to share
            one slab divided by a hairline, on the argument that they are three
            facts about the same frame —but a hairline is not enough to stop
            the eye reading straight past the match score on its way to the
            number that moves, and the match score is the one a spectator
            arriving mid-frame needs first.

            So the frame score gets its own block, half again as tall as the
            strip it used to be, with digits to match. It is a fixed height
            because it holds a fixed thing: two numerals and a colon, whatever
            the board's shape.

            Both cards are inset top and bottom against the player panels
            either side. It is the same trick a broadcast graphic uses to say
            which of three boxes is the subordinate one, and it costs nothing
            but the padding.

            The column's own gutter is the row's gutter. Three blocks stacked
            0.5rem apart inside a row spaced 0.75rem reads as a group that was
            squeezed to fit rather than as one column of three, and the eye
            picks up the mismatch long before it can name it. The break block
            is the one that gives up the difference, because it is the only
            one here sized by what is left over. */}
        <div
          className="flex w-full shrink-0 flex-col items-center gap-2 py-2 sm:gap-3
                     md:w-[clamp(11rem,24vw,15rem)] md:py-4"
        >
          <div
            className="hud-frame h-[28.9%] max-h-[11.4rem] min-h-[6.6rem] w-full shrink-0
                       overflow-hidden"
            style={QUIET_FRAME as CSSProperties}
          >
            <FramesBlock state={state} flat />
          </div>

          <div
            className="hud-frame flex min-h-0 w-full flex-1 flex-col items-center overflow-hidden
                       pt-4"
            style={QUIET_FRAME as CSSProperties}
          >
            <BreakBlock state={state} flat className="min-h-0 w-full flex-1"
              onAdjustReds={delta => void dispatch({ type: 'reds.adjusted', source: 'manual', delta })} />
          </div>

          <RemoteButton onOpen={() => setHandingOff(true)} />
        </div>

        <PlayerPanel
          state={state}
          player={1}
          avatarUrl={seats[1]?.player.avatarUrl ?? null}
          onTakeTable={live ? miss : undefined}
          onAddScore={() => setScoreEntry({ matchId, frameNumber: state.frameNumber, player: 1 })}
        />
      </div>

      <SuggestionQueue suggestions={suggestions} onResolve={(s, a) => void resolveSuggestion(s, a)} />

      {/* One bar, not three. The balls score, the fouls concede and the
          controls end a visit or a frame —they used to be a pad, a column
          beside it and a row underneath, which made the bottom half of the
          board a grid of panels to search rather than a console to operate.
          Read left to right it is now: what happened, then what to do. */}
      <TablePreview state={state} showPreview={showTablePreview} />
      {live && paused && (
        <p className="shrink-0 text-center text-xs uppercase tracking-[0.18em] text-sys-yellow">
          {pauseNotice}
        </p>
      )}
      {live && !paused && exhausted && (
        <p className="shrink-0 text-center text-xs uppercase tracking-[0.18em] text-sys-yellow">
          {t('match.exhausted')}
        </p>
      )}
      {remaking && (
        <ConfirmDialog title={t('match.remake')} message={t('match.remakeConfirm')}
          confirmLabel={t('match.remakeGo')} onCancel={() => setRemaking(false)}
          onConfirm={() => { setRemaking(false); remakeFrame(); }} />
      )}
      {frameWinnerRequired && (
        <FrameWinnerDialog players={state.players} disabled={saving} error={error}
          onSelect={player => void finishFrame(player)} onCancel={cancelFrameWinner} />
      )}
      <ActionBar
        state={state}
        onPot={pot}
        foulFollowUp={paused ? <RestorationControls state={state} onChanged={refresh} disabled={saving} /> : undefined}
        onFoul={foul}
        foulDisabled={!live || (paused && state.pauseReason !== 'waiting_for_respot')}
        onFreeBall={setFreeBall}
        onSwitch={miss}
        onUndo={() => void undoLast()}
        onEndFrame={() => void finishFrame()}
        onFoulBack={() => void returnToOffender()}
        endFramePending={saving}
        disabled={!live || paused}
      />

      <FrameOverlay state={state} onNextFrame={() => void startNextFrame()} onNewMatch={leave}
        saving={saving} error={error}
        onResume={() => {
          if (state.idleClosure && !saving) {
            void dispatch({ type: 'match.resumed', source: 'manual', closedAt: state.idleClosure.at });
          }
        }} />

      {scoreEntry?.matchId === matchId && scoreEntry.frameNumber === state.frameNumber && live && (
        <ScoreEntryDialog
          playerName={state.players[scoreEntry.player]}
          disabled={paused}
          error={error}
          onClose={() => setScoreEntry(null)}
          onConfirm={async (points, clientId) => {
            const saved = await dispatch({ type: 'score.added', source: 'manual', player: scoreEntry.player, points, endTurn: true, clientId });
            if (saved) setScoreEntry(current => current === scoreEntry ? null : current);
            return saved;
          }}
        />
      )}

      {handingOff && (
        <RemotePanel
          matchId={matchId}
          connected={connected}
          onClose={() => setHandingOff(false)}
        />
      )}

      {aiNotice && <AiNotice message={aiNotice} onClose={() => setAiNotice(null)} />}
      {leaving && (
        <ConfirmDialog
          title={t('leave.title')}
          message={t('leave.message', { result: frameResult(state.framesWon, state.players, t) })}
          confirmLabel={t('leave.confirm')}
          onConfirm={() => void endAndLeave()}
          onCancel={() => setLeaving(false)}
        />
      )}
      </main>
    </div>
  );
}

/**
 * How the match stands right now, in words, for the leave warning.
 *
 * Frames won decide it rather than points: a player leading on points in an
 * unfinished frame has not won it, and saying otherwise would misreport the
 * result somebody is about to accept.
 */
function frameResult(
  framesWon: readonly [number, number],
  players: readonly [string, string],
  t: (key: MessageKey, vars?: Record<string, string | number>) => string,
): string {
  const [a, b] = framesWon;
  if (a === b) return t('leave.resultDraw', { a, b });
  const winner = a > b ? 0 : 1;
  return t('leave.resultWin', {
    name: players[winner],
    a: Math.max(a, b),
    b: Math.min(a, b),
  });
}
